import React, { useEffect, useMemo, useState } from "react";
import {
  deleteField,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { db, storage } from "../../../firebase";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import useGameCatalog from "../../../hooks/useGameCatalog";

const parseDateValue = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toLocalInputValue = (value) => {
  const date = parseDateValue(value);
  if (!date) return typeof value === "string" ? value : "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const sanitizeFileName = (name = "file") =>
  String(name).replace(/[^a-zA-Z0-9._-]/g, "_");

const buildRulesDraft = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((rule, index) => {
        if (typeof rule === "string") {
          return { title: `Rule ${index + 1}`, description: rule };
        }
        return {
          title: String(rule?.title || `Rule ${index + 1}`),
          description: String(rule?.description || ""),
        };
      })
      .filter((rule) => rule.title.trim() || rule.description.trim());
  }
  if (typeof value === "string" && value.trim()) {
    return [{ title: "Rule 1", description: value.trim() }];
  }
  return [];
};

const buildPrizeDraft = (value) =>
  Array.isArray(value)
    ? value.map((item) => ({
        position: String(item?.position ?? ""),
        amount: String(item?.amount ?? ""),
      }))
    : [];

const buildPlacementDraft = (value) =>
  Array.isArray(value)
    ? value.map((item) => ({
        position: String(item?.position ?? ""),
        points: String(item?.points ?? ""),
      }))
    : [];

const buildListText = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "")).join("\n");
  }
  if (typeof value === "string") return value;
  return "";
};

const buildDraft = (tournament) => ({
  tournamentName: String(tournament?.tournamentName || ""),
  gameId: String(tournament?.gameId || ""),
  prizePool: String(tournament?.prizePool ?? 0),
  startsOn: toLocalInputValue(tournament?.startsOn),
  endsOn: toLocalInputValue(tournament?.endsOn),
  registrationEndsOn: toLocalInputValue(tournament?.registrationEndsOn),
  shortDescription: String(tournament?.shortDescription || ""),
  checkInTime: String(tournament?.checkInTime || ""),
  type: String(tournament?.type || "Team"),
  minTeamSize: String(tournament?.minTeamSize ?? 1),
  maxRegistrations: String(tournament?.maxRegistrations ?? 100),
  tournamentBanner: String(tournament?.tournamentBanner || ""),
  tournamentImage: String(tournament?.tournamentImage || ""),
  howToJoinText: buildListText(tournament?.howToJoin),
  termsText: buildListText(tournament?.terms),
  rules: buildRulesDraft(tournament?.rules),
  prizeDistribution: buildPrizeDraft(tournament?.prizeDistribution),
  placementPoints: buildPlacementDraft(tournament?.placementPoints),
});

const serializeRules = (rules) =>
  (Array.isArray(rules) ? rules : [])
    .map((rule, index) => ({
      title: String(rule?.title || "").trim() || `Rule ${index + 1}`,
      description: String(rule?.description || "").trim(),
    }))
    .filter((rule) => rule.description);

const serializeTextList = (value) =>
  String(value || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);

const serializePrizeDistribution = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => ({
      position: Number(item?.position),
      amount: Number(item?.amount),
    }))
    .filter(
      (item) => !Number.isNaN(item.position) && !Number.isNaN(item.amount)
    );

const serializePlacementPoints = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => ({
      position: Number(item?.position),
      points: Number(item?.points),
    }))
    .filter(
      (item) => !Number.isNaN(item.position) && !Number.isNaN(item.points)
    );

const serializeDraft = (draft) => ({
  tournamentName: String(draft?.tournamentName || "").trim(),
  gameId: String(draft?.gameId || "").trim(),
  prizePool: String(draft?.prizePool || "").trim(),
  startsOn: String(draft?.startsOn || "").trim(),
  endsOn: String(draft?.endsOn || "").trim(),
  registrationEndsOn: String(draft?.registrationEndsOn || "").trim(),
  shortDescription: String(draft?.shortDescription || "").trim(),
  checkInTime: String(draft?.checkInTime || "").trim(),
  type: String(draft?.type || "Team"),
  minTeamSize: String(draft?.minTeamSize || "").trim(),
  maxRegistrations: String(draft?.maxRegistrations || "").trim(),
  tournamentBanner: String(draft?.tournamentBanner || "").trim(),
  tournamentImage: String(draft?.tournamentImage || "").trim(),
  howToJoinText: String(draft?.howToJoinText || "").trim(),
  termsText: String(draft?.termsText || "").trim(),
  rules: serializeRules(draft?.rules),
  prizeDistribution: serializePrizeDistribution(draft?.prizeDistribution),
  placementPoints: serializePlacementPoints(draft?.placementPoints),
});

const TournamentSettingsPanel = ({
  tournament,
  registeredCount,
  roundsCount,
  onTournamentUpdated,
}) => {
  const { user: currentUser, role: currentRole } = useAdminAuth();
  const { games, loading: gamesLoading, getGameConfig } = useGameCatalog();
  const initialDraft = useMemo(() => buildDraft(tournament), [tournament]);
  const [draft, setDraft] = useState(initialDraft);
  const [savingTournament, setSavingTournament] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [uploadProgress, setUploadProgress] = useState({});

  useEffect(() => {
    setDraft(initialDraft);
    setSaveError("");
    setSaveMessage("");
    setUploadProgress({});
  }, [initialDraft]);

  const selectedGameConfig = useMemo(
    () => getGameConfig(draft.gameId),
    [draft.gameId, getGameConfig]
  );
  const isBattleRoyale = selectedGameConfig?.format === "battle_royale";
  const initialSerialized = useMemo(
    () => JSON.stringify(serializeDraft(initialDraft)),
    [initialDraft]
  );
  const currentSerialized = useMemo(
    () => JSON.stringify(serializeDraft(draft)),
    [draft]
  );
  const hasUnsavedChanges = initialSerialized !== currentSerialized;
  const hasActiveUploads = Object.keys(uploadProgress).length > 0;
  const hasExistingStructure = registeredCount > 0 || roundsCount > 0;

  const gameOptions = useMemo(() => {
    const list = Array.isArray(games) ? [...games] : [];
    if (!draft.gameId || list.some((game) => game.id === draft.gameId)) {
      return list;
    }
    return [{ id: draft.gameId, name: draft.gameId, active: false }, ...list];
  }, [draft.gameId, games]);

  const updateDraftField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const updateRule = (index, field, value) => {
    setDraft((prev) => ({
      ...prev,
      rules: prev.rules.map((rule, ruleIndex) =>
        ruleIndex === index ? { ...rule, [field]: value } : rule
      ),
    }));
  };

  const updatePrize = (index, field, value) => {
    setDraft((prev) => ({
      ...prev,
      prizeDistribution: prev.prizeDistribution.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const updatePlacementPoint = (index, field, value) => {
    setDraft((prev) => ({
      ...prev,
      placementPoints: prev.placementPoints.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const removeRule = (index) => {
    setDraft((prev) => ({
      ...prev,
      rules: prev.rules.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const removePrize = (index) => {
    setDraft((prev) => ({
      ...prev,
      prizeDistribution: prev.prizeDistribution.filter(
        (_, itemIndex) => itemIndex !== index
      ),
    }));
  };

  const removePlacementPoint = (index) => {
    setDraft((prev) => ({
      ...prev,
      placementPoints: prev.placementPoints.filter(
        (_, itemIndex) => itemIndex !== index
      ),
    }));
  };

  const handleMediaUpload = (field, file) => {
    if (!tournament?.id || !file) return;
    setSaveError("");
    setSaveMessage("");

    const storageRef = ref(
      storage,
      `tournament-images/${tournament.id}/${field}/${Date.now()}_${sanitizeFileName(
        file.name
      )}`
    );
    const task = uploadBytesResumable(storageRef, file);

    setUploadProgress((prev) => ({ ...prev, [field]: 0 }));

    task.on(
      "state_changed",
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setUploadProgress((prev) => ({ ...prev, [field]: progress }));
      },
      (error) => {
        console.error(`Failed to upload ${field}`, error);
        setSaveError(
          `Failed to upload ${
            field === "tournamentBanner" ? "banner" : "image"
          }.`
        );
        setUploadProgress((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          setDraft((prev) => ({ ...prev, [field]: url }));
          setSaveMessage("Media uploaded. Save tournament to publish the change.");
        } catch (error) {
          console.error(`Failed to resolve ${field} URL`, error);
          setSaveError(
            "Upload completed but the file URL could not be resolved."
          );
        } finally {
          setUploadProgress((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
          });
        }
      }
    );
  };

  const handleSaveTournament = async () => {
    if (!tournament?.id) return;

    const tournamentName = draft.tournamentName.trim();
    const gameId = draft.gameId.trim();
    const startsOn = draft.startsOn.trim();
    const endsOn = draft.endsOn.trim();
    const registrationEndsOn = draft.registrationEndsOn.trim();
    const prizePool = Number(draft.prizePool);
    const minTeamSize = Number(draft.minTeamSize);
    const maxRegistrations = Number(draft.maxRegistrations);

    if (!tournamentName) {
      setSaveError("Tournament name is required.");
      return;
    }
    if (!gameId) {
      setSaveError("Select a game.");
      return;
    }
    if (!startsOn || !endsOn || !registrationEndsOn) {
      setSaveError("Start, end, and registration end dates are required.");
      return;
    }
    if (Number.isNaN(prizePool) || prizePool < 0) {
      setSaveError("Prize pool must be 0 or more.");
      return;
    }
    if (Number.isNaN(minTeamSize) || minTeamSize <= 0) {
      setSaveError("Minimum team size must be at least 1.");
      return;
    }
    if (Number.isNaN(maxRegistrations) || maxRegistrations <= 0) {
      setSaveError("Max registrations must be at least 1.");
      return;
    }

    const howToJoin = serializeTextList(draft.howToJoinText);
    const terms = serializeTextList(draft.termsText);
    const rules = serializeRules(draft.rules);
    const prizeDistribution = serializePrizeDistribution(draft.prizeDistribution);
    const placementPoints = isBattleRoyale
      ? serializePlacementPoints(draft.placementPoints)
      : [];

    setSavingTournament(true);
    setSaveError("");
    setSaveMessage("");

    try {
      const tournamentRef = doc(db, "tournaments", tournament.id);
      await updateDoc(tournamentRef, {
        tournamentName,
        gameId,
        prizePool,
        startsOn,
        endsOn,
        registrationEndsOn,
        shortDescription: draft.shortDescription.trim(),
        checkInTime: draft.checkInTime.trim(),
        type: draft.type === "Solo" ? "Solo" : "Team",
        minTeamSize,
        maxRegistrations,
        tournamentBanner: draft.tournamentBanner.trim(),
        tournamentImage: draft.tournamentImage.trim(),
        prizeDistribution,
        placementPoints,
        howToJoin: howToJoin.length ? howToJoin : deleteField(),
        rules: rules.length ? rules : deleteField(),
        terms: terms.length ? terms : deleteField(),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.uid || null,
        updatedByRole: currentRole || null,
      });

      const tournamentSnap = await getDoc(tournamentRef);
      if (tournamentSnap.exists()) {
        onTournamentUpdated({
          id: tournamentSnap.id,
          ...tournamentSnap.data(),
        });
      }
      setSaveMessage("Tournament updated.");
    } catch (error) {
      console.error("Failed to update tournament", error);
      setSaveError(error?.message || "Failed to update tournament.");
    } finally {
      setSavingTournament(false);
    }
  };

  if (!tournament) return null;

  return (
    <div className="card form-card">
      <div className="card-header">
        <div>
          <h3>Tournament settings</h3>
          <p className="helper-text">
            Edit live tournament metadata, rewards, content, and media.
          </p>
        </div>
        <span className="chip">
          {hasUnsavedChanges ? "Unsaved changes" : "Up to date"}
        </span>
      </div>

      {saveError ? <div className="alert error">{saveError}</div> : null}
      {saveMessage ? <div className="alert info">{saveMessage}</div> : null}
      {hasExistingStructure ? (
        <div className="alert info tournament-risk-note">
          <span>
            This tournament already has {registeredCount} registrations and{" "}
            {roundsCount} rounds. Changing game, type, or registration rules can
            make existing entries inconsistent.
          </span>
        </div>
      ) : null}

      <div className="form-section">
        <div className="section-title">Core details</div>
        <div className="form-grid">
          <label className="field">
            <span>Tournament Name</span>
            <input
              type="text"
              value={draft.tournamentName}
              onChange={(event) =>
                updateDraftField("tournamentName", event.target.value)
              }
            />
          </label>

          <label className="field">
            <span>Game</span>
            <select
              value={draft.gameId}
              onChange={(event) => updateDraftField("gameId", event.target.value)}
              disabled={gamesLoading}
            >
              {!gameOptions.length ? (
                <option value="">No games available</option>
              ) : null}
              {gameOptions.map((game) => (
                <option key={game.id} value={game.id}>
                  {game.name}
                  {game.active === false ? " (inactive)" : ""}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Type</span>
            <select
              value={draft.type}
              onChange={(event) => updateDraftField("type", event.target.value)}
            >
              <option value="Team">Team</option>
              <option value="Solo">Solo</option>
            </select>
          </label>

          <label className="field">
            <span>Prize Pool (INR)</span>
            <input
              type="number"
              min="0"
              value={draft.prizePool}
              onChange={(event) =>
                updateDraftField("prizePool", event.target.value)
              }
            />
          </label>

          <label className="field">
            <span>Min Team Size</span>
            <input
              type="number"
              min="1"
              value={draft.minTeamSize}
              onChange={(event) =>
                updateDraftField("minTeamSize", event.target.value)
              }
            />
          </label>

          <label className="field">
            <span>Max Registrations</span>
            <input
              type="number"
              min="1"
              value={draft.maxRegistrations}
              onChange={(event) =>
                updateDraftField("maxRegistrations", event.target.value)
              }
            />
          </label>

          <label className="field">
            <span>Starts On</span>
            <input
              type="datetime-local"
              value={draft.startsOn}
              onChange={(event) =>
                updateDraftField("startsOn", event.target.value)
              }
            />
          </label>

          <label className="field">
            <span>Ends On</span>
            <input
              type="datetime-local"
              value={draft.endsOn}
              onChange={(event) => updateDraftField("endsOn", event.target.value)}
            />
          </label>

          <label className="field">
            <span>Registration Ends On</span>
            <input
              type="datetime-local"
              value={draft.registrationEndsOn}
              onChange={(event) =>
                updateDraftField("registrationEndsOn", event.target.value)
              }
            />
          </label>

          <label className="field">
            <span>Check-in Time</span>
            <input
              type="text"
              value={draft.checkInTime}
              onChange={(event) =>
                updateDraftField("checkInTime", event.target.value)
              }
            />
          </label>

          <label className="field field-wide">
            <span>Short Description</span>
            <textarea
              className="tournament-long-text"
              value={draft.shortDescription}
              onChange={(event) =>
                updateDraftField("shortDescription", event.target.value)
              }
            />
          </label>
        </div>
      </div>

      <div className="divider" />

      <div className="form-section">
        <div className="section-title-row">
          <div className="section-title">Prize Distribution</div>
          <button
            type="button"
            className="button ghost small"
            onClick={() =>
              setDraft((prev) => ({
                ...prev,
                prizeDistribution: [
                  ...prev.prizeDistribution,
                  { position: "", amount: "" },
                ],
              }))
            }
          >
            Add Prize
          </button>
        </div>
        {draft.prizeDistribution.length ? (
          draft.prizeDistribution.map((item, index) => (
            <div key={`prize-${index}`} className="tournament-pair-row">
              <input
                type="number"
                min="1"
                placeholder="Position"
                value={item.position}
                onChange={(event) =>
                  updatePrize(index, "position", event.target.value)
                }
              />
              <input
                type="number"
                min="0"
                placeholder="Amount"
                value={item.amount}
                onChange={(event) =>
                  updatePrize(index, "amount", event.target.value)
                }
              />
              <button
                type="button"
                className="button danger small"
                onClick={() => removePrize(index)}
              >
                Remove
              </button>
            </div>
          ))
        ) : (
          <div className="helper-text">No prize distribution set.</div>
        )}
      </div>

      <div className="divider" />

      <div className="form-section">
        <div className="section-title-row">
          <div className="section-title">Placement Points</div>
          <button
            type="button"
            className="button ghost small"
            onClick={() =>
              setDraft((prev) => ({
                ...prev,
                placementPoints: [
                  ...prev.placementPoints,
                  { position: "", points: "" },
                ],
              }))
            }
            disabled={!isBattleRoyale}
          >
            Add Rule
          </button>
        </div>
        {isBattleRoyale ? (
          draft.placementPoints.length ? (
            draft.placementPoints.map((item, index) => (
              <div key={`placement-${index}`} className="tournament-pair-row">
                <input
                  type="number"
                  min="1"
                  placeholder="Placement"
                  value={item.position}
                  onChange={(event) =>
                    updatePlacementPoint(index, "position", event.target.value)
                  }
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Points"
                  value={item.points}
                  onChange={(event) =>
                    updatePlacementPoint(index, "points", event.target.value)
                  }
                />
                <button
                  type="button"
                  className="button danger small"
                  onClick={() => removePlacementPoint(index)}
                >
                  Remove
                </button>
              </div>
            ))
          ) : (
            <div className="helper-text">
              No placement points configured for this battle royale tournament.
            </div>
          )
        ) : (
          <div className="helper-text">
            Placement points are only used for battle royale formats.
          </div>
        )}
      </div>

      <div className="divider" />

      <div className="form-section">
        <div className="section-title-row">
          <div className="section-title">Rules</div>
          <button
            type="button"
            className="button ghost small"
            onClick={() =>
              setDraft((prev) => ({
                ...prev,
                rules: [...prev.rules, { title: "", description: "" }],
              }))
            }
          >
            Add Rule
          </button>
        </div>
        {draft.rules.length ? (
          draft.rules.map((rule, index) => (
            <div key={`rule-${index}`} className="tournament-rule-row">
              <input
                type="text"
                placeholder="Rule title"
                value={rule.title}
                onChange={(event) =>
                  updateRule(index, "title", event.target.value)
                }
              />
              <textarea
                placeholder="Rule description"
                value={rule.description}
                onChange={(event) =>
                  updateRule(index, "description", event.target.value)
                }
              />
              <button
                type="button"
                className="button danger small"
                onClick={() => removeRule(index)}
              >
                Remove
              </button>
            </div>
          ))
        ) : (
          <div className="helper-text">
            Leave empty to use the app default rules content.
          </div>
        )}
      </div>

      <div className="divider" />

      <div className="form-section">
        <div className="section-title">Tournament copy</div>
        <div className="form-grid">
          <label className="field field-wide">
            <span>How to Join</span>
            <textarea
              className="tournament-long-text"
              value={draft.howToJoinText}
              onChange={(event) =>
                updateDraftField("howToJoinText", event.target.value)
              }
            />
            <div className="helper-text">
              One step per line. Leave blank to use the default content.
            </div>
          </label>

          <label className="field field-wide">
            <span>Terms and Conditions</span>
            <textarea
              className="tournament-long-text"
              value={draft.termsText}
              onChange={(event) =>
                updateDraftField("termsText", event.target.value)
              }
            />
            <div className="helper-text">
              One item per line. Leave blank to use the default content.
            </div>
          </label>
        </div>
      </div>

      <div className="divider" />

      <div className="form-section">
        <div className="section-title">Media</div>
        <div className="tournament-media-grid">
          <div className="tournament-media-card">
            <div className="tournament-media-preview">
              {draft.tournamentBanner ? (
                <img src={draft.tournamentBanner} alt="" />
              ) : (
                <span className="icon-placeholder">No banner</span>
              )}
            </div>
            <div className="tournament-media-actions">
              <label className="field">
                <span>Banner URL</span>
                <input
                  type="text"
                  value={draft.tournamentBanner}
                  onChange={(event) =>
                    updateDraftField("tournamentBanner", event.target.value)
                  }
                />
              </label>
              <div className="inline-actions">
                <label className="button ghost small file-upload">
                  Upload banner
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      handleMediaUpload(
                        "tournamentBanner",
                        event.target.files?.[0]
                      )
                    }
                  />
                </label>
                {draft.tournamentBanner ? (
                  <button
                    type="button"
                    className="button ghost small"
                    onClick={() => updateDraftField("tournamentBanner", "")}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
              {typeof uploadProgress.tournamentBanner === "number" ? (
                <div className="upload-progress">
                  Uploading {uploadProgress.tournamentBanner}%
                </div>
              ) : (
                <div className="helper-text">
                  Landscape image used on tournament list cards and hero banners.
                </div>
              )}
            </div>
          </div>

          <div className="tournament-media-card">
            <div className="tournament-media-preview portrait">
              {draft.tournamentImage ? (
                <img src={draft.tournamentImage} alt="" />
              ) : (
                <span className="icon-placeholder">No image</span>
              )}
            </div>
            <div className="tournament-media-actions">
              <label className="field">
                <span>Portrait Image URL</span>
                <input
                  type="text"
                  value={draft.tournamentImage}
                  onChange={(event) =>
                    updateDraftField("tournamentImage", event.target.value)
                  }
                />
              </label>
              <div className="inline-actions">
                <label className="button ghost small file-upload">
                  Upload image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      handleMediaUpload(
                        "tournamentImage",
                        event.target.files?.[0]
                      )
                    }
                  />
                </label>
                {draft.tournamentImage ? (
                  <button
                    type="button"
                    className="button ghost small"
                    onClick={() => updateDraftField("tournamentImage", "")}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
              {typeof uploadProgress.tournamentImage === "number" ? (
                <div className="upload-progress">
                  Uploading {uploadProgress.tournamentImage}%
                </div>
              ) : (
                <div className="helper-text">
                  Portrait image used as the fallback tournament artwork.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="button ghost"
          onClick={() => {
            setDraft(initialDraft);
            setSaveError("");
            setSaveMessage("");
          }}
          disabled={savingTournament || hasActiveUploads || !hasUnsavedChanges}
        >
          Reset
        </button>
        <button
          type="button"
          className="button primary"
          onClick={handleSaveTournament}
          disabled={savingTournament || hasActiveUploads || !hasUnsavedChanges}
        >
          {savingTournament ? "Saving..." : "Save tournament"}
        </button>
      </div>
    </div>
  );
};

export default TournamentSettingsPanel;
